import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoService } from '@jsverse/transloco';

import { AuthService } from '../../services/auth.service';
import { BrowserApiService } from '../../services/browser-api.service';
import { UserService } from '../../services/user.service';

/** Guest-only persistence key — logged-in users persist via their account's `language` field instead. */
export const GUEST_LANGUAGE_KEY = 'guest-language';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './language-switcher.html',
  styleUrl: './language-switcher.scss',
})
export class LanguageSwitcherComponent {
  availableLangs: string[];
  activeLang: string;

  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly browserApi = inject(BrowserApiService);

  constructor() {
    this.availableLangs = this.translocoService.getAvailableLangs() as string[];
    this.activeLang = this.translocoService.getActiveLang();
  }

  /**
   * Applies the choice immediately, then persists it: to the account's
   * `language` field when logged in (the same field that also drives email
   * language — see AccountComponent), or to localStorage for a guest, so it
   * survives reloads until they log in and get an account-level preference.
   */
  setActiveLang(lang: string): void {
    this.translocoService.setActiveLang(lang);
    this.activeLang = lang;

    const user = this.authService.getUserData();
    if (this.authService.isConnected() && user) {
      this.userService.updateUser(user.id, { language: lang }).subscribe();
    } else {
      this.browserApi.localStorage?.setItem(GUEST_LANGUAGE_KEY, lang);
    }
  }
}
